import axios from "axios";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { BiBookAlt, BiLogOut, BiX, BiBarChartAlt2 } from "react-icons/bi";

/* ─── Styled Components ─────────────────────────────────────────────────── */
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  opacity: ${({ open }) => open ? 1 : 0};
  pointer-events: ${({ open }) => open ? "auto" : "none"};
  transition: opacity 0.3s ease;
`;

const StyledMenu = styled.nav`
  z-index: 1001;
  display: flex;
  flex-direction: column;
  background: #fff;
  transform: ${({ open }) => open ? "translateX(0)" : "translateX(-100%)"};
  height: 100vh;
  width: 260px;
  position: fixed;
  top: 0; left: 0;
  transition: transform 0.3s ease-in-out;
  box-shadow: 4px 0 24px rgba(0,0,0,0.12);
`;

/* Menu header */
const MenuHeader = styled.div`
  background: linear-gradient(135deg, #0D3318 0%, #237A35 100%);
  padding: 32px 20px 24px;
`;

const MenuLogo = styled.div`
  font-size: 22px; font-weight: 800; color: #fff;
  letter-spacing: -0.5px; margin-bottom: 2px;
`;

const MenuSub = styled.div`
  font-size: 11px; color: rgba(255,255,255,0.6); letter-spacing: 0.05em;
`;

/* Menu items */
const MenuBody = styled.div`
  flex: 1; padding: 16px 12px; display: flex; flex-direction: column; gap: 4px;
`;

const MenuItem = styled.button`
  display: flex; align-items: center; gap: 12px;
  padding: 13px 14px; border-radius: 12px;
  border: none; background: none; width: 100%;
  text-align: left; cursor: pointer; font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 600;
  color: ${({ $danger }) => $danger ? "#DC2626" : "#374151"};
  transition: all .15s;
  &:hover {
    background: ${({ $danger }) => $danger ? "#FEF2F2" : "#F0FDF4"};
    color: ${({ $danger }) => $danger ? "#B91C1C" : "#237A35"};
  }
`;

const MenuDivider = styled.div`
  height: 1px; background: #F3F4F6; margin: 8px 14px;
`;

const IconWrap = styled.div`
  width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $danger }) => $danger ? "#FEF2F2" : "#F0FDF4"};
  color: ${({ $danger }) => $danger ? "#DC2626" : "#237A35"};
`;

const MenuFooter = styled.div`
  padding: 16px 20px; border-top: 1px solid #F3F4F6;
  font-size: 10px; color: #4B5563; text-align: center;
`;

/* ─── PDF Viewer Modal ──────────────────────────────────────────────────── */
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1100;
  background: rgba(0,0,0,0.6);
  display: flex; flex-direction: column;
`;

const ModalHeader = styled.div`
  background: #0D3318;
  padding: 14px 18px;
  display: flex; align-items: center; justify-content: space-between;
  flex-shrink: 0;
`;

const ModalTitle = styled.div`
  font-size: 15px; font-weight: 700; color: #fff;
  display: flex; align-items: center; gap: 8px;
`;

const CloseBtn = styled.button`
  width: 34px; height: 34px; border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.1);
  color: #fff; font-size: 18px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
`;

const PdfFrame = styled.iframe`
  flex: 1; border: none; background: #525659;
`;

const PdfFallback = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: #525659; gap: 14px; color: #fff;
  text-align: center; padding: 24px;
`;

const OpenExtBtn = styled.a`
  padding: 10px 20px; border-radius: 10px;
  background: #237A35; color: #fff; text-decoration: none;
  font-size: 13px; font-weight: 700; font-family: 'Inter', sans-serif;
`;

/* ─── Component ─────────────────────────────────────────────────────────── */
const Menu = ({ open, setOpen, ...props }) => {
  const [showPdf, setShowPdf] = useState(false);

  const logoutUser = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_API}/user/logout`, {
        withCredentials: true
      });
    } catch { /* proceed anyway */ }
    localStorage.removeItem("accessToken");
    localStorage.removeItem("org_id");
    window.location.href = "/";
  };

  const pdfUrl = `${window.location.origin}/User-Manual.pdf`;

  // iOS Safari can't scroll a PDF inside an <iframe> — it sticks on page 1 and
  // only the X works. Detect iOS (incl. iPadOS reporting as Mac) and open the
  // manual in a new tab, where the native PDF viewer scrolls normally. Other
  // platforms keep the in-app modal viewer.
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const openManual = () => {
    setOpen(false);
    if (isIOS) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } else {
      setShowPdf(true);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <Overlay open={open} onClick={() => setOpen(false)} />

      {/* Slide-in menu */}
      <StyledMenu open={open} {...props}>
        <MenuHeader>
          <MenuLogo>Vaaripa</MenuLogo>
          <MenuSub>Water wise</MenuSub>
        </MenuHeader>

        <MenuBody>
          <MenuItem as={Link} to="/reports" onClick={() => setOpen(false)}>
            <IconWrap><BiBarChartAlt2 size={18} /></IconWrap>
            Reports
          </MenuItem>

          <MenuItem onClick={openManual}>
            <IconWrap><BiBookAlt size={18} /></IconWrap>
            User Manual
          </MenuItem>

          <MenuDivider />

          <MenuItem $danger onClick={logoutUser}>
            <IconWrap $danger><BiLogOut size={18} /></IconWrap>
            Logout
          </MenuItem>
        </MenuBody>

        <MenuFooter>© 2026 Vaaripa Innovations</MenuFooter>
      </StyledMenu>

      {/* In-app PDF viewer modal */}
      {showPdf && (
        <ModalOverlay>
          <ModalHeader>
            <ModalTitle>
              <BiBookAlt size={18} />
              User Manual
            </ModalTitle>
            <CloseBtn onClick={() => setShowPdf(false)}>
              <BiX size={20} />
            </CloseBtn>
          </ModalHeader>

          <PdfFrame
            src={pdfUrl}
            title="User Manual"
            onError={() => {}}
          />

          {/* Fallback shown below iframe on mobile if iframe is blocked */}
          <div style={{
            background: "#0D3318", padding: "10px 18px",
            display: "flex", justifyContent: "space-between",
            alignItems: "center", flexShrink: 0,
          }}>
            <span style={{fontSize:11,color:"rgba(255,255,255,.6)"}}>
              Can't see the PDF? Open it directly.
            </span>
            <OpenExtBtn href={pdfUrl} download="Vaaripa-User-Manual.pdf">
              Download PDF
            </OpenExtBtn>
          </div>
        </ModalOverlay>
      )}
    </>
  );
};

export default Menu;
